import React, { useRef } from 'react';
import cls from 'classnames';
import PropTypes from 'prop-types';
import addYears from 'date-fns/addYears';
import startOfToday from 'date-fns/startOfToday';
import { Field } from 'redux-form';
import FirstErrorShower from '@ott/first-error-shower';
import Datepicker from '@ott/datepicker';
import {
  PICKUP_DATE,
  DROPOFF_DATE,
  PICKUP_TIME,
  DROPOFF_TIME,
  PICKUP_FIELD_NAME,
  DROPOFF_FIELD_NAME,
  VALIDATE_MESSAGES,
  AGE_FIELD_NAME,
} from '../consts';

import CarsTimepicker from './blocks/CarsTimepicker';
import LocationReturnSwitcher from './blocks/LocationReturnSwitcher';
import DropdownForCars from './blocks/DropdownForCars';
import Autocomplete from './blocks/Autocomplete';
import SearchButton from './blocks/SearchButton';

import styles from './CarsSearchForm.scss';

function CarsSearchLayout(props) {
  const {
    formRef,
    submitRef,
    handleLocationTypeChange,
    isSameLocation,
    className,
    handleSubmitCarsSearchForm,
    errors,
    isShowError,
    submitTime,
    handleSubmit,
    pickUpPoints,
    handleChangeDirection,
    valueForAutocomplete,
    pickUpActualValue,
    handleSelectPoint,
    dropOffPoints,
    dropOffActualValue,
    handleDataSelect,
    change,
    isDesktopLayout,
    pickUpDateValue = {},
  } = props;

  const newFormRef = useRef();
  const resultFormRef = formRef || newFormRef;

  const forceLongMonth = isDesktopLayout ? isSameLocation : true;
  const maxAvailableRentDate = addYears(startOfToday(), 1);

  return (
    <form
      ref={resultFormRef}
      id="CarsSearchForm"
      className={cls(styles.CarsSearchForm, className)}
      onSubmit={handleSubmit(handleSubmitCarsSearchForm)}
      data-locator="CarsSearchForm"
      data-gtm-locator="CarsSearchForm"
    >
      <FirstErrorShower
        errors={errors}
        show={isShowError}
        container={resultFormRef.current}
        effect="transition"
        time={submitTime}
        tooltipClassName={styles.reducedZindex}
      />
      <div
        className={cls(styles.topRow, {
          [styles.differentReturnPlaces]: !isSameLocation,
        })}
      >
        <div className={styles.placeInputsWrapper}>
          <div className={styles.from}>
            <Field
              name={PICKUP_FIELD_NAME}
              component={Autocomplete}
              overlayProps={{
                options: pickUpPoints,
                className: styles.customAutocompleteWidth,
              }}
              triggerProps={{
                title: 'Место получения',
                onSearch: handleChangeDirection(PICKUP_FIELD_NAME),
                placeholder: 'Город, место получения',
                className: styles.from,
              }}
              props={{
                openOnFocus: false,
                shouldRenderAirportCity: true,
                value: valueForAutocomplete(pickUpActualValue),
                onChange: handleSelectPoint(PICKUP_FIELD_NAME),
              }}
            />
          </div>
        </div>

        <div className={styles.fromDateTimeFields}>
          <div className={styles.datePicker}>
            <Field
              name={PICKUP_DATE}
              component={Datepicker}
              props={{
                maxDate: maxAvailableRentDate,
                closeAfterSelect: true,
                placeholder: 'Когда',
                pickOneDate: true,
                forceLongMonth,
                title: 'Дата получения',
                onDateSelect: handleDataSelect(PICKUP_DATE),
                childrenClassNames: {
                  input: styles.round,
                },
              }}
            />
          </div>

          <div className={styles.timeTakeWrapper}>
            <CarsTimepicker
              change={change}
              name={PICKUP_TIME}
              classNames={{ inputTrigger: styles.timeTake }}
            />
          </div>
        </div>

        {!isSameLocation && (
          <div className={styles.dropInput}>
            <Field
              name={DROPOFF_FIELD_NAME}
              component={Autocomplete}
              triggerProps={{
                title: isShowError ? VALIDATE_MESSAGES.dropOff : null,
                onSearch: handleChangeDirection(DROPOFF_FIELD_NAME),
                placeholder: 'Город, место возврата',
                className: styles.from,
              }}
              overlayProps={{
                options: dropOffPoints,
                className: cls(styles.customAutocompleteWidth, styles.customAutocompletePosition),
              }}
              props={{
                value: valueForAutocomplete(dropOffActualValue),
                onChange: handleSelectPoint(DROPOFF_FIELD_NAME),
                shouldRenderAirportCity: true,
              }}
            />
          </div>
        )}

        <div className={styles.dropDatesWrapper}>
          <div className={styles.dropDatesFields}>
            <div className={styles.datePicker}>
              <Field
                component={Datepicker}
                name={DROPOFF_DATE}
                props={{
                  placeholder: 'Когда',
                  minDate: pickUpDateValue.startDate,
                  maxDate: maxAvailableRentDate,
                  pickOneDate: true,
                  closeAfterSelect: true,
                  forceLongMonth,
                  title: 'Дата возврата',
                  onDateSelect: handleDataSelect(DROPOFF_DATE),
                  calendarPlacement: 'right',
                  childrenClassNames: {
                    calendar: styles.dropOffDateCustomPosition,
                    input: styles.round,
                  },
                }}
              />
            </div>

            <div className={styles.timeReturnWrapper}>
              <CarsTimepicker
                change={change}
                name={DROPOFF_TIME}
                classNames={{ inputTrigger: styles.timeReturn }}
              />
            </div>
          </div>

          <div className={styles.mobileLocationSwitcher}>
            <LocationReturnSwitcher
              specificLocator="mobile"
              onClick={handleLocationTypeChange}
              isSameLocationReturn={!isSameLocation}
            />
          </div>
        </div>
        <SearchButton innerRef={submitRef} />
      </div>

      <div className={styles.LocationReturnSwitcher}>
        <div className={styles.desktopLocationSwitcher}>
          <LocationReturnSwitcher
            specificLocator="desktop"
            onClick={handleLocationTypeChange}
            isSameLocationReturn={!isSameLocation}
          />
        </div>
        <div className={styles.age}>
          <Field component={DropdownForCars} name={AGE_FIELD_NAME} />
        </div>
      </div>
    </form>
  );
}

CarsSearchLayout.propTypes = {
  className: PropTypes.string,
  handleSubmitCarsSearchForm: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleDataSelect: PropTypes.func,
  handleLocationTypeChange: PropTypes.func,
  handleChangeDirection: PropTypes.func,
  handleSelectPoint: PropTypes.func,
  errors: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
  submitTime: PropTypes.number,
  pickUpPoints: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.object)),
  valueForAutocomplete: PropTypes.func,
  pickUpActualValue: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
  dropOffPoints: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.object)),
  dropOffActualValue: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
  change: PropTypes.func.isRequired,
  isShowError: PropTypes.bool,
  isSameLocation: PropTypes.bool,
  isDesktopLayout: PropTypes.bool,
  pickUpDateValue: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
  }),
};

export default React.memo(CarsSearchLayout);
